<template>
  <tooltip
    v-if="clipboardSupported"
    v-model="showClipboardFeedback"
    :text="clipboardFeedbackText"
    trigger="manual"
    type="button"
    class="btn"
    :class="`${btnClass} ${ignoreBlurClass} ${returnFocusClass}`"
    ref="copyButton"
    data-clipboard-action="copy"
    :placement="tooltipPlacement"
    tag="button">
    <span/><!-- this is here as a hack to render the tooltip nicely above the button that the tooltip component is rendering -->
    <slot/>
  </tooltip>
</template>

<script>
import { toggleCopyOnBody, dispatchCopyEventFrom, getVisuallyHiddenText } from '@/util/copypaste_helpers'
import Clipboard from 'clipboard'
import { SharedClassNames } from '@/util/shared_classnames'
import { mapGetters } from 'vuex'
import { actionPermissions, ACTION_TYPES } from '@/util/access_visibility'

export default {
  name: 'CopyButton',
  props: {
    btnClass: {
      type: String,
      default: 'btn-default'
    },
    copyText: {
      type: String,
      required: true
    },
    successText: {
      type: String,
      default: 'Text copied'
    },
    errorText: {
      type: String,
      default: 'Could not copy text'
    },
    noText: {
      type: String,
      default: 'There is no text to copy'
    },
    tooltipPlacement: {
      type: String,
      default: 'top'
    }
  },
  data() {
    return {
      clipboard: null,
      clipboardSupported: Clipboard.isSupported(),
      clipboardFeedbackText: '',
      showClipboardFeedback: false,
      ignoreBlurClass: SharedClassNames.IGNORE_BLUR,
      returnFocusClass: SharedClassNames.RETURN_FOCUS
    }
  },
  computed: {
    ...mapGetters('user', ['authInfo']),
    ...mapGetters('contentAccess', ['contentAccessStatus']),
    copyPermissions() {
      return actionPermissions(
        ACTION_TYPES.COPY,
        '',
        {},
        this.authInfo,
        this.contentAccessStatus
      )
    },
  },
  mounted() {
    const body = document.getElementsByTagName('body')[0]
    let clipboard = new Clipboard(this.$refs.copyButton.$el, {
      text: () => {
        if (!this.copyPermissions.block) {
          toggleCopyOnBody(true)
          if (!this.copyText) {
            // Announce status text to screen readers
            const visuallyHiddenText = getVisuallyHiddenText(this.noText)
            body.append(visuallyHiddenText)
            setTimeout(() => {
              visuallyHiddenText.remove()
            }, 2500)
          }
          return this.copyText
        } else {
          dispatchCopyEventFrom(this.$refs.copyButton.$el, true)
        }
      }
    })
    clipboard.on('success', e => {
      this.clipboardFeedbackText = this.successText
      this.showClipboardFeedback = true

      // Announce status text to screen readers
      const visuallyHiddenText = getVisuallyHiddenText(this.successText)
      body.append(visuallyHiddenText)

      // Copying via this button will not trigger a browser event, which means the copy won't be tracked by an ancestor CopyTrackWrapper. Thus we simulate a copy event so that we can track it as a normal copy
      dispatchCopyEventFrom(e.trigger, true) // e.trigger is the DOM element that invoked the copy action

      this.$emit('copied')
      setTimeout(() => {
        this.showClipboardFeedback = false
        visuallyHiddenText.remove()
      }, 2500)

      toggleCopyOnBody(false)
      e.clearSelection()
    })

    clipboard.on('error', e => {
      this.clipboardFeedbackText = this.errorText
      // Announce status text to screen readers
      const visuallyHiddenText = getVisuallyHiddenText(this.errorText)
      body.append(visuallyHiddenText)

      setTimeout(() => {
        this.showClipboardFeedback = false
        visuallyHiddenText.remove()
      }, 4000)

      toggleCopyOnBody(false)
    })
    this.clipboard = clipboard
  },
  beforeDestroy() {
    if (this.clipboard) {
      this.clipboard.destroy()
    }
  }
}
</script>

<style lang="less" scoped>
.btn {
  position: relative;
  span {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
  }
}
</style>]
